'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ChevronDown,
  Copy,
  FileClock,
  FileDown,
  FilePlus,
  Film,
  Flag,
  FolderPlus,
  Heart,
  HeartIcon,
  Search,
  Share,
  WandSparkles
} from 'lucide-react';
import Link from 'next/link';
import { useShareModal } from './share-modal';
import { getPostUpscales, likePost, unlikePost } from '@/lib/api/post';
import { siteConfigurations } from '../shared/sites/sites';
import { useGlobalContext } from '@/app/context/store';
import { useSignInModal } from '../layout/sign-in-modal';
import { useReportModal } from './reportModal';
import AddToAlbumModal from './addToAlbumModal';
import axios from 'axios';
import { useGenerator } from '@/app/context/GeneratorContext';
import UpscaleButton from '../upscale/upscaleButton';
import UpscaledImagesModal from '../upscale/upscaleImagesModal';
import { useFlags } from '@/lib/hooks/useFlags';
import ResponsiveToolbar, { ToolbarButton } from './responsiveToolbar';
import { getLocalizedPath } from '../shared/formatURL';
import { useDownloadModal } from './downloadModal';
import { findStyleName } from '../shared/generators/generators';

export interface PostUpscaleData {
  upscaleGenerationId: string;
  upscaleFactor: number;
  upscaleUrl: string;
  createdAt: string;
  width: number;
  height: number;
}

const ImageComponent = ({
  action,
  _id,
  type = 'image',
  translations,
  modalTranslations,
  signInModalTranslations,
  reportModalTranslations,
  shareModalTranslations,
  upscaleTranslations,
  downloadModalTranslations
}: {
  action: any;
  _id: string;
  type?: 'image' | 'gif';
  translations: any;
  modalTranslations: any;
  signInModalTranslations: any;
  reportModalTranslations: any;
  shareModalTranslations: any;
  upscaleTranslations: any;
  downloadModalTranslations: any;
}) => {
  const router = useRouter();
  const localizedPostSearchPath = getLocalizedPath(type === 'image' ? 'post/search' : 'gif/search');
  const searchPath = localizedPostSearchPath + '?search=' + encodeURIComponent(action.randomTag);
  const [downloading, setDownloading] = useState(false);
  // const [likes, setLikes] = useState(action.post.likes || 0);
  const [liked, setLiked] = useState(false);
  const [styleName, setStyleName] = useState('');
  const [isAddToAlbumOpen, setIsAddToAlbumOpen] = useState(false);
  const [isUpscaledImagesModalOpen, setIsUpscaledImagesModalOpen] = useState(false);
  const { user } = useGlobalContext();
  const { startUpscale, gifGeneratorList } = useGenerator();
  const { data: flagsData } = useFlags();
  const image2gifEnabledFlag = flagsData?.['image2gif-enabled'];
  const { ShareModal, setShowShareModal } = useShareModal(shareModalTranslations);

  const { ReportModal, setShowReportModal } = useReportModal(reportModalTranslations);

  const { SignInModal, setShowSignInModal } = useSignInModal(signInModalTranslations);

  const { DownloadModal, setShowDownloadModal } = useDownloadModal(downloadModalTranslations);

  const [upscales, setUpscales] = useState<PostUpscaleData[]>([]);
  const isDev =
    process.env.NEXT_PUBLIC_BASE_URL === 'http://localhost:3000' ||
    process.env.NEXT_PUBLIC_BASE_URL === 'https://www.8porn.ai';
  const matchingOrigin =
    siteConfigurations[action.post?.origin]?.siteUrl === process.env.NEXT_PUBLIC_BASE_URL;
  const shouldRender = isDev || matchingOrigin;
  const isAuthor = action.post?.author === user._id;
  const [upscalesLoading, setUpscalesLoading] = useState(true);

  useEffect(() => {
    const fallback = setTimeout(() => setStyleName(action.post?.generator?.shortName), 4000);

    const interval = setInterval(() => {
      const style = findStyleName(translations.key, action.post?.generator?.shortName);
      if (style) {
        clearTimeout(fallback);
        clearInterval(interval);
        setStyleName(style);
      }
    }, 100);

    return () => {
      clearTimeout(fallback);
      clearInterval(interval);
    };
  }, [action.post?.generator?.shortName, translations.key]);

  const downloadImage = async (url: string) => {
    if (user._id === 'none') {
      // window.alert(locale.accessViaAccount);
      setShowSignInModal(true);
    } else {
      setDownloading(true);

      const response = await axios.get(url + '?original=1', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${_id}.${url.endsWith('.png') ? 'png' : 'jpg'}`;
      link.click();

      // Release the reference to the object URL after the download starts
      URL.revokeObjectURL(objectUrl);

      setDownloading(false);
    }
  };

  const downloadGif = async (url: string) => {
    if (user._id === 'none') {
      setShowSignInModal(true);
    } else {
      setDownloading(true);

      try {
        const response = await axios.get(url, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], {
          type: response.headers['content-type']
        });
        const objectUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${_id}.mp4`;
        link.click();

        // Release the reference to the object URL after the download starts
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Error downloading GIF:', error);
      } finally {
        setDownloading(false);
      }
    }
  };

  const handleClick = () => {
    if (user._id === 'none') {
      setShowSignInModal(true);
      // window.alert(locale.accessViaAccount);
      return;
    }

    if (liked) {
      //@ts-ignore
      unlikePost(action.post._id);
    } else {
      //@ts-ignore
      likePost(action.post._id);
    }

    setLiked((prevLiked: any) => !prevLiked);
    // setLikes((prevLikes: number) => (!liked ? prevLikes + 1 : prevLikes - 1));
  };

  const getCloneUrl = () => {
    let newCustomImagePrompt = action.post?.customPrompt || '';
    let newCustomGifPrompt = action.post?.gifPrompt || '';

    let postType = action.post?.type;
    let gifType = postType === 'gif' && action.post?.referenceImageId ? 'chained' : '';
    let style =
      action.post?.generator?.shortName === 'i2vtest'
        ? gifGeneratorList.find((g) => g._id === action.post?.referenceImageId?.generator)
            ?.shortName || action.post?.generator?.shortName
        : action.post?.generator?.shortName;
    let url =
      action.localePath +
      '/create?type=' +
      type +
      '&style=' +
      style +
      '&resolution=' +
      action.post?.aspectRatio +
      '&tags=' +
      action.tagIds +
      '&prompt=' +
      encodeURIComponent(type === 'image' || gifType === 'chained' ? newCustomImagePrompt : '');
    // If gif, include both gifPrompt and prompt. Both populated means its image2Gif.
    if (postType === 'gif') {
      url +=
        '&gifPrompt=' +
        encodeURIComponent(gifType === 'chained' ? newCustomGifPrompt : newCustomImagePrompt) +
        '&gifType=' +
        gifType +
        '&length=' +
        action.post?.length +
        '&frame=' +
        action.post?.frameRate;
    }
    return url;
  };

  const handleCloneClick = (e: React.MouseEvent) => {
    if (user._id === 'none') {
      e.preventDefault();
      setShowSignInModal(true);
    }
    // If user is authenticated, let the Link handle navigation naturally
  };

  const getI2GUrl = () => {
    return action.localePath + '/create?image=' + action.post._id;
  };

  const getExtendGifUrl = () => {
    return action.localePath + '/create?gif=' + action.post._id;
  };

  const upscaleMedia = useCallback(
    async (factor: number) => {
      await startUpscale(action.post._id, factor);
      router.push(`${action.localePath}/create`);
    },
    [action.post._id, startUpscale, router, action.localePath]
  );

  const getUpscales = async () => {
    const upscales = await getPostUpscales(action.post._id);
    setUpscales(upscales.postUpscalesResponse?.data.upscales || []);
    setUpscalesLoading(false);
  };

  useEffect(() => {
    setLiked(action.post?.userLiked);
  }, [action.post?.userLiked]);

  const showUpscaleElements = () => {
    if (action.post.author === user._id) {
      return true;
    } else {
      return false;
    }
  };

  const handleDownloadClick = () => {
    if (user._id === 'none') {
      setShowSignInModal(true);
    } else if (action.post.author !== user._id) {
      setShowDownloadModal(true);
    } else {
      if (type === 'gif') {
        downloadGif(action.post.videoUrl);
      } else {
        downloadImage(action.post.imageUrl);
      }
    }
  };

  useEffect(() => {
    getUpscales();
  }, [action.post.upscales]);

  const buttonsReady = !upscalesLoading && !!action.post && !!flagsData;

  const upscaleButtonStyle =
    'inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-crp-blue px-4 py-2';
  const upscaleButtonContent = (
    <>
      <WandSparkles size={16} />
      <span>{upscaleTranslations.upscale}</span>
      <ChevronDown size={20} />
    </>
  );

  const imageButtons: ToolbarButton[] = [
    {
      id: 'save',
      buttonType: 'action',
      label: liked ? translations.saved : translations.save,
      onClick: () => handleClick(),
      icon: <Heart color="white" size={16} fill={liked ? '#ffffff' : 'none'} />,
      className: `inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-grey-outline px-4 py-2 ${
        liked ? 'bg-crp-red' : 'bg-grey-button'
      } hover:brightness-105`
    },
    {
      id: 'report',
      buttonType: 'action',
      label: translations.report,
      onClick: () => {
        if (user._id === 'none') {
          setShowSignInModal(true);
        } else {
          setShowReportModal(true);
        }
      },
      icon: <Flag size={16} />
    },
    {
      id: 'viewHistory',
      buttonType: 'action',
      label: translations.viewHistory,
      icon: <FolderPlus size={16} />,
      onClick: () => setIsUpscaledImagesModalOpen(true),
      show: showUpscaleElements() && upscales && upscales.length > 0
    },
    {
      id: 'share',
      buttonType: 'action',
      label: translations.share,
      icon: <Share size={16} />,
      onClick: () => setShowShareModal(true)
    },
    {
      id: 'download',
      buttonType: 'action',
      label: translations.download,
      icon: <FileDown size={16} />,
      onClick: () => handleDownloadClick(),
      disabled: downloading
    },
    {
      id: 'viewSimilar',
      buttonType: 'link',
      label: translations.viewSimilar,
      icon: <Search size={16} />,
      href:
        localizedPostSearchPath +
        '?search=' +
        encodeURIComponent(!action.randomTag ? styleName : action.randomTag),
      show: shouldRender
    }
  ];

  const gifButtons: ToolbarButton[] = [
    {
      id: 'extend',
      buttonType: user._id === 'none' ? 'action' : 'link',
      label: translations.extendGif,
      onClick: () => setShowSignInModal(true),
      href: getExtendGifUrl(),
      icon: <Film size={16} />,
      className: `inline-flex min-w-fit flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-crp-blue px-4 py-2 hover:brightness-110`,
      show: image2gifEnabledFlag?.enabled && action.post?.frameRate !== 24 && shouldRender
    },
    {
      id: 'addToAlbum',
      buttonType: 'action',
      label: translations.addToAlbum,
      onClick: () => {
        if (user._id === 'none') {
          setShowSignInModal(true);
        } else {
          setIsAddToAlbumOpen(true);
        }
      },
      icon: <FolderPlus size={16} />
    },
    {
      id: 'save',
      buttonType: 'action',
      label: liked ? translations.saved : translations.save,
      onClick: () => handleClick(),
      icon: <Heart size={16} />,
      show: isAuthor,
      className: `inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-grey-outline px-4 py-2 ${
        liked ? 'bg-crp-red' : 'bg-grey-button'
      } hover:brightness-105`
    },
    {
      id: 'viewSimilar',
      buttonType: 'link',
      label: translations.viewSimilar,
      icon: <Search size={16} />,
      href: localizedPostSearchPath + '?search=' + encodeURIComponent(styleName),
      show: !isAuthor && shouldRender
    },
    {
      id: 'save',
      buttonType: 'action',
      label: liked ? translations.saved : translations.save,
      onClick: () => handleClick(),
      icon: <Heart size={16} />,
      show: !isAuthor,
      className: `inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-grey-outline px-4 py-2 ${
        liked ? 'bg-crp-red' : 'bg-grey-button'
      } hover:brightness-105`
    },
    {
      id: 'report',
      buttonType: 'action',
      label: translations.report,
      onClick: () => {
        if (user._id === 'none') {
          setShowSignInModal(true);
        } else {
          setShowReportModal(true);
        }
      },
      icon: <Flag size={16} />
    },
    {
      id: 'viewHistory',
      buttonType: 'action',
      label: translations.viewHistory,
      icon: <FileClock size={16} />,
      onClick: () => setIsUpscaledImagesModalOpen(true),
      show: showUpscaleElements() && upscales && upscales.length > 0
    },
    {
      id: 'share',
      buttonType: 'action',
      label: translations.share,
      icon: <Share size={16} />,
      onClick: () => setShowShareModal(true)
    },
    {
      id: 'download',
      buttonType: 'action',
      label: translations.download,
      icon: <FileDown size={16} />,
      onClick: () => handleDownloadClick(),
      disabled: downloading
    },
    {
      id: 'viewSimilar',
      buttonType: 'link',
      label: translations.viewSimilar,
      icon: <Search size={16} />,
      href: localizedPostSearchPath + '?search=' + encodeURIComponent(styleName),
      show: isAuthor && shouldRender
    }
  ];

  const placeHolderButtons = (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex flex-row gap-2">
          {Array.from({ length: 3 }).map((_, colIndex) => (
            <div
              key={colIndex}
              className="inline-flex h-10 min-w-fit flex-1 animate-pulse items-center justify-center gap-2 whitespace-nowrap rounded-md border border-grey-outline bg-grey-button px-4 py-2 duration-100"
            ></div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={`mb-auto w-auto border-[0.5px] border-b-0 border-[#3C3C3C] bg-[#2B2D31] shadow-md ${
        isDev ? false : !matchingOrigin && 'sm:rounded-bl-md sm:rounded-br-md'
      }`}
    >
      <ShareModal />
      <ReportModal />
      <SignInModal />
      <DownloadModal />
      {showUpscaleElements() && action.post.upscales?.length > 0 && type !== 'gif' && (
        <UpscaledImagesModal
          showModal={isUpscaledImagesModalOpen}
          setShowModal={setIsUpscaledImagesModalOpen}
          upscales={upscales}
          originalImage={{
            id: action.post._id,
            url: action.post.imageUrl
          }}
          translations={{
            ...upscaleTranslations,
            original: upscaleTranslations.original || 'Original'
          }}
          displayedUpscaleUrl={action.post.displayedUpscaleUrl}
        />
      )}
      <AddToAlbumModal
        showAddToAlbumModal={isAddToAlbumOpen}
        setShowAddToAlbumModal={setIsAddToAlbumOpen}
        postIds={[_id]}
        translations={{
          addToAlbum: modalTranslations.addToAlbum,
          albumName: modalTranslations.albumName,
          albumDescription: modalTranslations.albumDescription,
          cancel: modalTranslations.cancel,
          create: modalTranslations.create,
          add: modalTranslations.add,
          createAlbum: modalTranslations.createAlbum,
          added: modalTranslations.added
        }}
        signInModalTranslations={signInModalTranslations}
        contentType={type}
      />
      <div className="flex flex-col gap-2 bg-[#2B2D31] px-5 py-4 text-sm text-white transition-all duration-300">
        {!buttonsReady ? (
          <>{placeHolderButtons}</>
        ) : (
          <>
            <div className="flex flex-row">
              {type == 'image' &&
              image2gifEnabledFlag?.enabled &&
              (isDev ? true : matchingOrigin) ? (
                <>
                  {user._id === 'none' ? (
                    <button
                      onClick={() => setShowSignInModal(true)}
                      disabled={false}
                      className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-crp-red px-4 py-2 text-white hover:brightness-110"
                    >
                      <Film size={16} /> <span>{translations.imageToGif}</span>
                    </button>
                  ) : (
                    <Link
                      href={getI2GUrl()}
                      prefetch={false}
                      className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-crp-red px-4 py-2 text-white hover:brightness-110"
                    >
                      <Film size={16} /> <span>{translations.imageToGif}</span>
                    </Link>
                  )}
                </>
              ) : (
                shouldRender && (
                  <Link
                    href={getCloneUrl()}
                    onClick={handleCloneClick}
                    prefetch={false}
                    className={`inline-flex h-10 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md hover:brightness-110 ${
                      type === 'image' ? 'bg-crp-blue' : 'bg-crp-red'
                    } px-4 py-2`}
                  >
                    <Copy size={16} />
                    <span>{translations.cloneTags}</span>
                  </Link>
                )
              )}
            </div>
            {type === 'image' ? (
              <>
                <div className="flex w-full flex-row flex-wrap gap-2">
                  {shouldRender &&
                    action.post.author === user._id &&
                    action.post.displayedUpscaleFactor === 0 && (
                      <UpscaleButton
                        generationId={action.post._id}
                        onUpscale={upscaleMedia}
                        upscaleTranslations={upscaleTranslations}
                        upscaleButtonStyle={upscaleButtonStyle}
                        upscaleButtonContent={upscaleButtonContent}
                      />
                    )}

                  <div className="flex flex-auto flex-wrap gap-2">
                    {shouldRender && (
                      <Link
                        href={getCloneUrl()}
                        onClick={handleCloneClick}
                        prefetch={false}
                        className="inline-flex min-w-fit flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-crp-blue px-4 py-2 hover:brightness-110"
                      >
                        <Copy size={16} />
                        <span>{translations.cloneTags}</span>
                      </Link>
                    )}
                    <button
                      className="inline-flex min-w-fit flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-grey-outline bg-grey-button px-4 py-2 hover:brightness-110"
                      onClick={() => {
                        if (user._id === 'none') {
                          setShowSignInModal(true);
                        } else {
                          setIsAddToAlbumOpen(true);
                        }
                      }}
                      disabled={false}
                    >
                      <FolderPlus size={16} /> <span>{translations.addToAlbum}</span>
                    </button>
                  </div>
                </div>
                <ResponsiveToolbar type="image" items={imageButtons} moreText={translations.more} />
              </>
            ) : (
              <>
                <ResponsiveToolbar type="gif" items={gifButtons} moreText={translations.more} />{' '}
              </>
            )}
          </>
        )}
      </div>
      {(isDev ? false : !matchingOrigin) && (
        <div className="border-t-[0.5px] border-[#3C3C3C] pt-4">
          <div className="flex flex-row px-5 pb-4">
            <div className="text-sm font-medium text-white">
              {translations.madeWith}
              <a
                href={
                  siteConfigurations[action.post?.origin]?.siteUrl +
                    translations.path +
                    'post/' +
                    action.post?._id || 'https://www.createporn.com'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="mx-1 text-white underline"
              >
                {siteConfigurations[action.post?.origin]?.siteName || 'CreatePorn'}
                .com
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageComponent;
